import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { CloudUpload, Download, CheckCircle, Error, Visibility } from '@mui/icons-material';
import { bookImportService } from '../services/bookImportService';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';
import { logger } from '../utils/logger';


interface BookImportError {
  row: number;
  field: string;
  error: string;
  value: string;
}

interface BookImportSuccess {
  row: number;
  title: string;
  author: string;
  bookId: string;
}

interface BookImportResult {
  success: boolean;
  totalRows: number;
  successfulImports: number;
  failedImports: number;
  errors: BookImportError[];
  successes: BookImportSuccess[];
  message: string;
}

interface BookImportProps {
  onViewList?: () => void;
  onImportSuccess?: () => void;
}

export const BookImport: React.FC<BookImportProps> = ({ onViewList, onImportSuccess }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState<BookImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasImported, setHasImported] = useState(false);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Force re-read file to avoid browser cache issues
  const getFreshFile = async (originalFile: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const blob = new Blob([arrayBuffer], { type: originalFile.type });
          const freshFile = new File([blob], originalFile.name, {
            type: originalFile.type,
            lastModified: Date.now() // Force new timestamp
          });
          logger.log('🔄 File re-read:', {
            original: { name: originalFile.name, size: originalFile.size, lastModified: originalFile.lastModified },
            fresh: { name: freshFile.name, size: freshFile.size, lastModified: freshFile.lastModified }
          });
          resolve(freshFile);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      // Read as ArrayBuffer to preserve binary data
      reader.readAsArrayBuffer(originalFile);
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      logger.log('📁 File selected:', selectedFile.name, 'Size:', selectedFile.size, 'LastModified:', selectedFile.lastModified);

      // Force fresh file reading to avoid cache
      const freshFile = await getFreshFile(selectedFile);
      logger.log('📄 Fresh file created:', freshFile.name, 'Size:', freshFile.size, 'LastModified:', freshFile.lastModified);

      // Log file content immediately after selection
      await logFileContent(freshFile);

      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv' // .csv
      ];

      if (!allowedTypes.includes(freshFile.type) && !freshFile.name.endsWith('.csv')) {
        setError('Chỉ chấp nhận file Excel (.xlsx, .xls) hoặc CSV (.csv)');
        setFile(freshFile); // Still set the file to display it
        setImportResult(null);
        return;
      }

      // Validate file size (10MB limit)
      if (freshFile.size > 10 * 1024 * 1024) {
        setError('Kích thước file không được vượt quá 10MB');
        setFile(freshFile); // Still set the file to display it
        setImportResult(null);
        return;
      }

      // Validate CSV structure for CSV files
      if (freshFile.name.toLowerCase().endsWith('.csv')) {
        validateCSVStructure(freshFile);
      } else {
        // For Excel files, we'll validate after upload
        setFile(freshFile);
        setError(null);
        setImportResult(null);
      }
    }
  };

  const validateCSVStructure = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const lines = content.split('\n');

      // Always set the file first so it shows in UI
      setFile(file);
      setImportResult(null);

      if (lines.length < 2) {
        setError('File CSV phải có ít nhất 2 dòng (header + data)');
        return;
      }

      // Check header structure
      const header = lines[0].toLowerCase().trim();
      const requiredColumns = [
        'tên sách', 'tác giả', 'isbn', 'danh mục',
        'năm xuất bản', 'tổng số bản sao', 'số bản sao có sẵn', 'tóm tắt', 'chủ đề'
      ];

      const headerColumns = header.split(',').map(col => col.replace(/"/g, '').trim().toLowerCase());

      const missingColumns = requiredColumns.filter(col => !headerColumns.includes(col));

      if (missingColumns.length > 0) {
        setError(`File CSV thiếu các cột bắt buộc: ${missingColumns.join(', ')}`);
        return;
      }

      // Check if there's actual data
      const dataLines = lines.slice(1).filter(line => line.trim());
      if (dataLines.length === 0) {
        setError('File CSV không có dữ liệu');
        return;
      }

      // If validation passes, clear any previous errors
      setError(null);
    };

    reader.readAsText(file, 'UTF-8');
  };

  const handleRemoveFile = () => {
    setFile(null);
    setError(null);
    setImportResult(null);
    hideSnackbar(); // Close any open toast
    setHasImported(false); // Reset import state
    // Reset all file inputs
    const fileInputs = document.querySelectorAll('input[type="file"]') as NodeListOf<HTMLInputElement>;
    fileInputs.forEach(input => {
      input.value = '';
    });
  };

  // Log file content to debug
  const logFileContent = async (file: File): Promise<void> => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const content = e.target?.result as string;

        logger.log('📊 File Content Analysis:');
        logger.log('  📁 File:', file.name);
        logger.log('  📏 File size:', file.size, 'bytes');
        logger.log('  📋 File type:', file.type);

        // Check if it's a CSV file
        if (file.name.toLowerCase().endsWith('.csv')) {
          const lines = content.split('\n').filter(line => line.trim());
          logger.log('  📏 Total lines:', lines.length);
          logger.log('  📋 Header:', lines[0]);
          logger.log('  📝 Data rows:', lines.length - 1);

          if (lines.length > 1) {
            logger.log('  📄 Sample data rows:');
            lines.slice(1, 4).forEach((line, index) => {
              logger.log(`    Row ${index + 1}:`, line);
            });
          }
        } else {
          // For Excel files, we can't read them as text
          logger.log('  ⚠️ Excel file detected - content cannot be read as text');
          logger.log('  📄 Raw content preview (first 200 chars):', content.substring(0, 200));
          logger.log('  💡 Excel files need special parsing - will be processed by backend');
        }

        resolve();
      };

      reader.onerror = () => {
        logger.error('❌ Failed to read file content');
        resolve();
      };

      // For Excel files, we still try to read as text to see what happens
      reader.readAsText(file, 'UTF-8');
    });
  };

  const handleUpload = async () => {
    if (!file || isUploading || error) return; // Prevent multiple calls if file is invalid

    // Check if already imported this file
    if (hasImported && file) {
      setShowConfirmDialog(true);
      return;
    }

    setIsUploading(true);
    setError(null);
    setImportResult(null); // Clear previous result

    try {
      logger.log('🚀 Starting book import...', file.name);

      // Force re-read file to avoid cache issues
      const freshFile = await getFreshFile(file);
      logger.log('📄 Fresh file loaded:', freshFile.name, 'Size:', freshFile.size);

      // Log file content before import
      await logFileContent(freshFile);

      const result = await bookImportService.importBooks(freshFile);
      logger.log('✅ Import result received:', result);
      setImportResult(result);
      setHasImported(true);

      if (result.success) {
        // NEW: Trigger success callback
        if (onImportSuccess) {
          onImportSuccess();
        }

        if (result.failedImports === 0) {
          // Complete success
          showSuccess(`✅ Import thành công ${result.successfulImports}/${result.totalRows} sách`);
        } else {
          // Partial success
          showSuccess(`⚠️ Import hoàn thành: ${result.successfulImports}/${result.totalRows} sách thành công, ${result.failedImports} lỗi`);
        }
        // Reset form fields after successful import (keep importResult for display)
        setFile(null);
        setError(null);
        // Reset file input
        const fileInputs = document.querySelectorAll('input[type="file"]') as NodeListOf<HTMLInputElement>;
        fileInputs.forEach(input => {
          input.value = '';
        });
      } else {
        // Complete failure
        showError(`❌ ${result.message}`);
      }

    } catch (err) {
      logger.error('❌ Import error:', err);
      const errorMessage = err && typeof err === 'object' && 'message' in err
        ? (err as Error).message
        : 'Có lỗi xảy ra khi import sách';
      setError(errorMessage);

      // Show error toast
      showError(`❌ ${errorMessage}`);

    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    setShowConfirmDialog(false);
    // Import the same file directly and append to existing results
    setIsUploading(true);
    setError(null);

    try {
      logger.log('🚀 Starting duplicate import...', file?.name);

      // Force re-read file to avoid cache issues
      const freshFile = await getFreshFile(file!);
      logger.log('📄 Fresh file loaded for duplicate import:', freshFile.name, 'Size:', freshFile.size);

      // Log file content before import
      await logFileContent(freshFile);

      const result = await bookImportService.importBooks(freshFile);
      logger.log('✅ Duplicate import result received:', result);

      // NEW: Trigger success callback
      if (result.success && onImportSuccess) {
        onImportSuccess();
      }

      // Append new data to existing results
      if (importResult) {
        const combinedResult = {
          ...result,
          totalRows: importResult.totalRows + result.totalRows,
          successfulImports: importResult.successfulImports + result.successfulImports,
          failedImports: importResult.failedImports + result.failedImports,
          successes: [...importResult.successes, ...result.successes],
          errors: [...importResult.errors, ...result.errors],
          message: `Import tổng cộng: ${importResult.successfulImports + result.successfulImports}/${importResult.totalRows + result.totalRows} sách thành công`
        };
        setImportResult(combinedResult);

        // Show cumulative toast
        const totalSuccessful = importResult.successfulImports + result.successfulImports;
        const totalRows = importResult.totalRows + result.totalRows;
        const totalFailed = importResult.failedImports + result.failedImports;

        if (totalFailed === 0) {
          showSuccess(`✅ Import tổng cộng thành công ${totalSuccessful}/${totalRows} sách`);
        } else {
          showSuccess(`⚠️ Import tổng cộng: ${totalSuccessful}/${totalRows} sách thành công, ${totalFailed} lỗi`);
        }
      } else {
        setImportResult(result);
        showSuccess(`✅ Import thành công ${result.successfulImports}/${result.totalRows} sách`);
      }

    } catch (err) {
      logger.error('❌ Duplicate import error:', err);
      const errorMessage = err && typeof err === 'object' && 'message' in err
        ? (err as Error).message
        : 'Có lỗi xảy ra khi import sách';
      setError(errorMessage);
      showError(`❌ ${errorMessage}`);

    } finally {
      setIsUploading(false);
    }
  };


  const handleViewBookList = () => {
    if (onViewList) {
      onViewList();
    } else {
      navigate('/books');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await bookImportService.downloadTemplate();
    } catch (err) {
      const errorMessage = err && typeof err === 'object' && 'message' in err
        ? (err as Error).message
        : 'Có lỗi xảy ra khi tải template';
      setError(errorMessage);
    }
  };

  const resetForm = () => {
    handleRemoveFile();
    setImportResult(null);
    setHasImported(false);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Import Sách Hàng Loạt
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Sử dụng chức năng này để import nhiều sách cùng lúc từ file Excel hoặc CSV.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Bước 1: Tải Template
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tải file template Excel để biết cấu trúc dữ liệu cần thiết.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadTemplate}
            sx={{ mb: 2 }}
          >
            Tải Template Excel
          </Button>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Bước 2: Chọn File Import
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Chọn file Excel (.xlsx, .xls) hoặc CSV (.csv) chứa dữ liệu sách.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              sx={{ mr: 2 }}
            >
              Chọn File
              <input
                id="file-input"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </Button>

            {file && !importResult && (
              <Chip
                label={file.name}
                onDelete={handleRemoveFile}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ❌ File không hợp lệ
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                Vui lòng chọn file khác hoặc tải template để xem định dạng đúng.
              </Typography>
            </Alert>
          )}

          {file && !importResult && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={isUploading || !file || !!error} // Disable if no file, uploading, or has validation error
                sx={{ mr: 2 }}
              >
                {isUploading ? 'Đang Import...' : 'Bắt Đầu Import'}
              </Button>
              <Button
                variant="outlined"
                onClick={resetForm}
                disabled={isUploading}
              >
                Hủy
              </Button>
            </Box>
          )}

          {isUploading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Đang xử lý file...
              </Typography>
              <LinearProgress />
            </Box>
          )}
        </CardContent>
      </Card>

      {importResult && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              ✅ Kết Quả Import
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip
                  icon={<CheckCircle />}
                  label={`Thành công: ${importResult.successfulImports}`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<Error />}
                  label={`Thất bại: ${importResult.failedImports}`}
                  color="error"
                  variant="outlined"
                />
                <Chip
                  label={`Tổng: ${importResult.totalRows}`}
                  variant="outlined"
                />
              </Box>
            </Box>

            {importResult.successes.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Sách Import Thành Công
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Tên Sách</TableCell>
                        <TableCell>Tác Giả</TableCell>
                        <TableCell align="right">Dòng</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResult.successes.map((success, index) => (
                        <TableRow key={index}>
                          <TableCell>{success.bookId}</TableCell>
                          <TableCell>{success.title}</TableCell>
                          <TableCell>{success.author}</TableCell>
                          <TableCell align="right">{success.row}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {importResult.errors.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Lỗi Import
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Dòng</TableCell>
                        <TableCell>Trường</TableCell>
                        <TableCell>Lỗi</TableCell>
                        <TableCell>Giá Trị</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResult.errors.map((error, index) => (
                        <TableRow key={index}>
                          <TableCell>{error.row}</TableCell>
                          <TableCell>{error.field}</TableCell>
                          <TableCell>{error.error}</TableCell>
                          <TableCell>{error.value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Visibility />}
                onClick={handleViewBookList}
              >
                XEM DANH SÁCH SÁCH
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>⚠️ Cảnh báo Import Trùng Lặp</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn đã import file "{file?.name}" rồi. Bạn có chắc chắn muốn import lại không?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Import lại có thể tạo ra dữ liệu trùng lặp trong hệ thống.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Hủy
          </Button>
          <Button onClick={handleConfirmImport} variant="contained" color="warning">
            Tiếp Tục
          </Button>
        </DialogActions>
      </Dialog>

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Box>
  );
};
